import type { DateRange, AttributionFilterState } from '@/components/analytics';

import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { paths } from 'src/routes/paths';
import { useOrganization } from '@/context/organization-context';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FlowInsights } from '@/sections/flow-insights/flow-insights-view';
import {
  FlowFindingsDrawer,
  groupFindingsByFlow,
  maxSeverity,
  severityMuiColor,
} from '@/sections/flow-insights/flow-findings-drawer';
import { FlowDetailDrawer } from '@/components/analytics/FlowDetailDrawer';
import { MetricCard, PeriodSelector, TimeSeriesChart, AttributionControl, useDefaultDateRange } from '@/components/analytics';
import {
  useFlowAnalyticsControllerGetFlowList,
  useFlowAnalyticsControllerGetFlowOverview,
  useFlowAnalyticsControllerGetFlowFilterBounds,
} from '@/apis/backend/generated/analytics-flows/analytics-flows';
import { useFlowInsightsControllerGetInsights } from '@/apis/backend/generated/flow-insights/flow-insights';

import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { WarningAmberRounded as WarningIcon } from '@mui/icons-material';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Switch from '@mui/material/Switch';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import FormControlLabel from '@mui/material/FormControlLabel';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/flows')({
  component: FlowsAnalyticsPage,
});

interface FlowFilters {
  nameInclude?: string;
  nameExclude?: string;
  tags?: string[];
  statuses?: string[];
  showLiveOnly?: boolean;
}

function FlowsAnalyticsPage() {
  const { t } = useTranslation('analytics');
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(30);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [attribution, setAttribution] = useState<AttributionFilterState>({});
  const [activeTab, setActiveTab] = useState(0);

  // Flow Breakdown state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<FlowFilters>({});
  const [previewFlowId, setPreviewFlowId] = useState<string | null>(null);

  // Flow Comparison state
  const [compGroup1Range, setCompGroup1Range] = useState<DateRange>(defaultRange);
  const [compGroup2Range, setCompGroup2Range] = useState<DateRange>(defaultRange);
  const [compGroup1Flows, setCompGroup1Flows] = useState<string[]>([]);
  const [compGroup2Flows, setCompGroup2Flows] = useState<string[]>([]);

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;
  // The Insights tab (agency-style flow report) rides the existing Flows flag.
  const insightsEnabled = useFeatureFlag(FEATURE_FLAGS.FLOWS);

  // Pull the insights findings so the Flow Breakdown table can flag which flows
  // have setup/performance issues (a per-row warning icon → side panel). Shares
  // the React Query cache with the Insights tab (same rolling-period key).
  const { data: insights } = useFlowInsightsControllerGetInsights(
    orgId ?? '',
    clientId ?? '',
    undefined,
    { query: { enabled: insightsEnabled && !!orgId && !!clientId } }
  );
  const findingsByFlow = useMemo(
    () => groupFindingsByFlow(insights?.findings ?? []).byFlow,
    [insights?.findings]
  );
  const [findingsFlow, setFindingsFlow] = useState<{ name: string; flowId: string } | null>(null);

  const granularity = useMemo(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (days > 90) return 'month' as const;
    if (days > 21) return 'week' as const;
    return 'day' as const;
  }, [dateRange.startDate, dateRange.endDate]);

  const formatXAxis = useCallback((dateStr: string) => {
    const d = new Date(dateStr);
    if (granularity === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }, [granularity]);

  // API calls
  const { data: overview, isLoading: overviewLoading } = useFlowAnalyticsControllerGetFlowOverview(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      previousStartDate: dateRange.previousStartDate,
      previousEndDate: dateRange.previousEndDate,
      granularity,
      ...attribution,
    },
    { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } },
  );

  const { data: flowList, isLoading: listLoading } = useFlowAnalyticsControllerGetFlowList(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: page + 1,
      pageSize,
      sortBy,
      sortOrder,
      ...(filters.nameInclude ? { nameInclude: filters.nameInclude } : {}),
      ...(filters.nameExclude ? { nameExclude: filters.nameExclude } : {}),
      ...(filters.tags?.length ? { tags: filters.tags.join(',') } : {}),
      ...(filters.statuses?.length ? { statuses: filters.statuses.join(',') } : {}),
      ...(filters.showLiveOnly ? { showLiveOnly: true } : {}),
      ...attribution,
    },
    { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } },
  );

  const { data: filterBounds } = useFlowAnalyticsControllerGetFlowFilterBounds(
    { orgId: orgId!, clientId: clientId! },
    { query: { enabled: !!orgId && !!clientId, staleTime: 120000 } },
  );

  // Comparison data
  const { data: compGroup1Data } = useFlowAnalyticsControllerGetFlowList(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: compGroup1Range.startDate,
      endDate: compGroup1Range.endDate,
      page: 1,
      pageSize: 200,
    },
    { query: { enabled: !!orgId && !!clientId && activeTab === 2, staleTime: 60000 } },
  );

  const { data: compGroup2Data } = useFlowAnalyticsControllerGetFlowList(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: compGroup2Range.startDate,
      endDate: compGroup2Range.endDate,
      page: 1,
      pageSize: 200,
    },
    { query: { enabled: !!orgId && !!clientId && activeTab === 2, staleTime: 60000 } },
  );

  if (!orgId || !clientId) return null;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const kpis = overview?.kpis;

  const flowListItems = flowList?.items ?? [];
  const allFlowNames = [...(compGroup1Data?.items ?? []), ...(compGroup2Data?.items ?? [])];
  const uniqueFlowNames = Array.from(new Map(allFlowNames.map(f => [f.id, f])).values());

  // Comparison aggregation
  const aggregateFlows = (items: typeof flowListItems, selectedIds: string[]) => {
    const selected = selectedIds.length > 0 ? items.filter(f => selectedIds.includes(f.id)) : items;
    if (selected.length === 0) return null;
    const totalRevenue = selected.reduce((s, f) => s + f.totalRevenue, 0);
    const deliveries = selected.reduce((s, f) => s + f.deliveries, 0);
    const emailDeliveries = selected.reduce((s, f) => s + f.emailDeliveries, 0);
    const smsDeliveries = selected.reduce((s, f) => s + f.smsDeliveries, 0);
    const emailRevenue = selected.reduce((s, f) => s + f.emailRevenue, 0);
    const smsRevenue = selected.reduce((s, f) => s + f.smsRevenue, 0);
    const avgOpen = deliveries > 0 ? selected.reduce((s, f) => s + f.openRate * f.deliveries, 0) / deliveries : 0;
    const avgClick = deliveries > 0 ? selected.reduce((s, f) => s + f.clickRate * f.deliveries, 0) / deliveries : 0;
    return { totalRevenue, deliveries, emailDeliveries, smsDeliveries, emailRevenue, smsRevenue, avgOpenRate: avgOpen, avgClickRate: avgClick, flowCount: selected.length };
  };

  const group1Agg = compGroup1Data ? aggregateFlows(compGroup1Data.items, compGroup1Flows) : null;
  const group2Agg = compGroup2Data ? aggregateFlows(compGroup2Data.items, compGroup2Flows) : null;

  const columns = [
    { id: 'name', label: t('flows.columns.name', 'Flow Name') },
    { id: 'status', label: t('flows.columns.status', 'Status') },
    { id: 'revenue', label: t('flows.columns.revenue', 'Revenue') },
    { id: 'deliveries', label: t('flows.columns.deliveries', 'Deliveries') },
    { id: 'openRate', label: t('flows.columns.openRate', 'Open Rate') },
    { id: 'clickRate', label: t('flows.columns.clickRate', 'Click Rate') },
    { id: 'bounceRate', label: t('flows.columns.bounce', 'Bounce') },
    { id: 'revenuePerRecipient', label: t('flows.columns.revPerRecipient', 'Rev/Recipient') },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
        <PeriodSelector value={dateRange} onChange={setDateRange} />
        <AttributionControl value={attribution} onChange={setAttribution} />
      </Stack>

      {/* Internal tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('flows.tabs.overview', 'Flows Overview')} sx={{ textTransform: 'none' }} />
        <Tab label={t('flows.tabs.breakdown', 'Flow Breakdown')} sx={{ textTransform: 'none' }} />
        <Tab label={t('flows.tabs.comparison', 'Flow Comparison')} sx={{ textTransform: 'none' }} />
        {insightsEnabled && (
          <Tab label={t('flows.tabs.insights', 'Insights')} sx={{ textTransform: 'none' }} />
        )}
      </Tabs>

      {/* Tab 0: Flows Overview */}
      {activeTab === 0 && (
        <Stack spacing={3}>
          {/* KPI Cards */}
          {overviewLoading ? (
            <Stack direction="row" spacing={2}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: '12px' }} />
              ))}
            </Stack>
          ) : kpis ? (
            <>
              {/* Total */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t('flows.sections.totalPerformance', 'Total Flow Performance')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  <MetricCard title={t('flows.metrics.totalRevenue', 'Total Revenue')} metric={kpis.totalRevenue} format="currency" />
                  <MetricCard title={t('flows.metrics.totalDeliveries', 'Total Deliveries')} metric={kpis.totalDeliveries} format="number" />
                  <MetricCard title={t('flows.metrics.revPerRecipient', 'Rev / Recipient')} metric={kpis.revenuePerRecipient} format="currency" />
                </Box>
              </Box>
              {/* Email */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t('flows.sections.emailPerformance', 'Email Flow Performance')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                  <MetricCard title={t('flows.metrics.emailRevenue', 'Email Revenue')} metric={kpis.emailRevenue} format="currency" />
                  <MetricCard title={t('flows.metrics.emailDeliveries', 'Email Deliveries')} metric={kpis.emailDeliveries} format="number" />
                  <MetricCard title={t('flows.metrics.avgOpenRate', 'Avg Open Rate')} metric={kpis.avgOpenRate} format="percent" />
                  <MetricCard title={t('flows.metrics.avgClickRate', 'Avg Click Rate')} metric={kpis.avgClickRate} format="percent" />
                </Box>
              </Box>
              {/* SMS */}
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t('flows.sections.smsPerformance', 'SMS Flow Performance')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <MetricCard title={t('flows.metrics.smsRevenue', 'SMS Revenue')} metric={kpis.smsRevenue} format="currency" />
                  <MetricCard title={t('flows.metrics.smsDeliveries', 'SMS Deliveries')} metric={kpis.smsDeliveries} format="number" />
                </Box>
              </Box>
            </>
          ) : null}

          {/* Time Series Chart */}
          {overview?.timeSeries && overview.timeSeries.length > 0 && (
            <TimeSeriesChart
              title={t('flows.chart.title', 'Flow Performance')}
              data={overview.timeSeries}
              series={[
                { dataKey: 'emailRevenue', name: t('flows.metrics.emailRevenue', 'Email Revenue'), type: 'bar', color: theme.palette.primary.main, yAxisId: 'left' },
                { dataKey: 'smsRevenue', name: t('flows.metrics.smsRevenue', 'SMS Revenue'), type: 'bar', color: theme.palette.secondary.main, yAxisId: 'left' },
                { dataKey: 'emailDeliveries', name: t('flows.metrics.emailDeliveries', 'Email Deliveries'), type: 'line', color: theme.palette.primary.dark, yAxisId: 'right', dot: true },
                { dataKey: 'smsDeliveries', name: t('flows.metrics.smsDeliveries', 'SMS Deliveries'), type: 'line', color: theme.palette.secondary.dark, yAxisId: 'right', dot: true },
              ]}
              formatXAxis={formatXAxis}
              formatLeftAxis={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString()}`}
              formatRightAxis={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString()}
              formatTooltip={(value, name) =>
                String(name).includes('Revenue') ? `$${value.toLocaleString()}` : value.toLocaleString()
              }
            />
          )}
        </Stack>
      )}

      {/* Tab 1: Flow Breakdown */}
      {activeTab === 1 && (
        <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
          <Stack spacing={2} sx={{ p: 2, pb: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('flows.tabs.breakdown', 'Flow Breakdown')}</Typography>
            {/* Filters */}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                placeholder={t('flows.filters.includeName', 'Include name...')}
                value={filters.nameInclude || ''}
                onChange={(e) => { setFilters(f => ({ ...f, nameInclude: e.target.value || undefined })); setPage(0); }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                placeholder={t('flows.filters.excludeName', 'Exclude name...')}
                value={filters.nameExclude || ''}
                onChange={(e) => { setFilters(f => ({ ...f, nameExclude: e.target.value || undefined })); setPage(0); }}
                sx={{ width: 160 }}
              />
              {filterBounds?.tags && filterBounds.tags.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  options={filterBounds.tags}
                  value={filters.tags || []}
                  onChange={(_, v) => { setFilters(f => ({ ...f, tags: v.length > 0 ? v : undefined })); setPage(0); }}
                  renderInput={(params) => <TextField {...params} placeholder={t('flows.filters.tags', 'Tags')} />}
                  sx={{ minWidth: 180 }}
                />
              )}
              {filterBounds?.statuses && filterBounds.statuses.length > 0 && (
                <Autocomplete
                  multiple
                  size="small"
                  options={filterBounds.statuses}
                  value={filters.statuses || []}
                  onChange={(_, v) => { setFilters(f => ({ ...f, statuses: v.length > 0 ? v : undefined })); setPage(0); }}
                  renderInput={(params) => <TextField {...params} placeholder={t('flows.filters.status', 'Status')} />}
                  sx={{ minWidth: 140 }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={filters.showLiveOnly || false}
                    onChange={(e) => { setFilters(f => ({ ...f, showLiveOnly: e.target.checked || undefined })); setPage(0); }}
                  />
                }
                label={<Typography variant="body2">{t('flows.filters.liveOnly', 'Live only')}</Typography>}
              />
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortOrder : 'desc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {columns.map((col) => (
                        <TableCell key={col.id}><Skeleton width={80} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : flowListItems.length > 0 ? (
                  flowListItems.map((flow) => (
                    <TableRow key={flow.id} hover sx={{ cursor: 'pointer' }} onClick={() => setPreviewFlowId(flow.id)}>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {(() => {
                            const flowFindings = findingsByFlow.get(flow.id);
                            if (!flowFindings?.length) return null;
                            const sev = maxSeverity(flowFindings) || 'info';
                            return (
                              <Tooltip title={`${flowFindings.length} issue${flowFindings.length > 1 ? 's' : ''} to review`}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setFindingsFlow({ name: flow.name, flowId: flow.id }); }}
                                  sx={{ p: 0.25 }}
                                >
                                  <Badge badgeContent={flowFindings.length} color={severityMuiColor(sev) as any}>
                                    <WarningIcon fontSize="small" color={severityMuiColor(sev) as any} />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            );
                          })()}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, '&:hover': { color: 'primary.main' } }}>
                            {flow.name}
                          </Typography>
                          {flow.tags?.slice(0, 2).map((tag) => (
                            <Chip key={tag} label={tag} size="small" sx={{ fontSize: 10, height: 18 }} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={flow.status}
                          size="small"
                          color={flow.status === 'live' ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20 }}
                        />
                      </TableCell>
                      <TableCell>${parseFloat(String(flow.totalRevenue || 0)).toLocaleString()}</TableCell>
                      <TableCell>{flow.deliveries?.toLocaleString() || 0}</TableCell>
                      <TableCell>{flow.openRate?.toFixed(1)}%</TableCell>
                      <TableCell>{flow.clickRate?.toFixed(1)}%</TableCell>
                      <TableCell>{flow.bounceRate?.toFixed(1)}%</TableCell>
                      <TableCell>${parseFloat(String(flow.revenuePerRecipient || 0)).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {t('flows.empty.noFlows', 'No flows found for this period.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {flowList && (
            <TablePagination
              component="div"
              count={flowList.total || 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            />
          )}
        </Card>
      )}

      {/* Tab 2: Flow Comparison */}
      {activeTab === 2 && (
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Group 1 */}
            <Card sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>{t('flows.comparison.group1', 'Group 1')}</Typography>
              <PeriodSelector value={compGroup1Range} onChange={setCompGroup1Range} />
              <Autocomplete
                multiple
                size="small"
                options={uniqueFlowNames}
                getOptionLabel={(o) => o.name}
                value={uniqueFlowNames.filter(f => compGroup1Flows.includes(f.id))}
                onChange={(_, v) => setCompGroup1Flows(v.map(f => f.id))}
                renderInput={(params) => <TextField {...params} placeholder={t('flows.comparison.selectFlows', 'Select flows (empty = all)')} />}
                sx={{ mt: 2 }}
              />
            </Card>
            {/* Group 2 */}
            <Card sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>{t('flows.comparison.group2', 'Group 2')}</Typography>
              <PeriodSelector value={compGroup2Range} onChange={setCompGroup2Range} />
              <Autocomplete
                multiple
                size="small"
                options={uniqueFlowNames}
                getOptionLabel={(o) => o.name}
                value={uniqueFlowNames.filter(f => compGroup2Flows.includes(f.id))}
                onChange={(_, v) => setCompGroup2Flows(v.map(f => f.id))}
                renderInput={(params) => <TextField {...params} placeholder={t('flows.comparison.selectFlows', 'Select flows (empty = all)')} />}
                sx={{ mt: 2 }}
              />
            </Card>
          </Box>

          {/* Comparison Table */}
          {(group1Agg || group2Agg) && (
            <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'grey.200' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('flows.comparison.metricCol', 'Metric')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('flows.comparison.group1', 'Group 1')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('flows.comparison.group2', 'Group 2')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{t('flows.comparison.difference', 'Difference')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonRows(group1Agg, group2Agg, t).map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: row.isSection ? 600 : 400 }}>
                            {row.label}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{row.g1}</TableCell>
                        <TableCell align="right">{row.g2}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ color: row.diffColor, fontWeight: 500 }}
                          >
                            {row.diff}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Stack>
      )}

      {/* Tab 3: Insights (agency-style flow report) */}
      {insightsEnabled && activeTab === 3 && orgId && clientId && (
        <FlowInsights orgId={orgId} clientId={clientId} />
      )}

      <FlowDetailDrawer
        flowId={previewFlowId}
        onClose={() => setPreviewFlowId(null)}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        attribution={attribution}
      />

      <FlowFindingsDrawer
        open={!!findingsFlow}
        onClose={() => setFindingsFlow(null)}
        flowName={findingsFlow?.name ?? null}
        findings={findingsFlow ? findingsByFlow.get(findingsFlow.flowId) ?? [] : []}
        onOpenFlow={
          findingsFlow
            ? () => navigate({ to: paths.dashboard.flowDetail(findingsFlow.flowId) })
            : undefined
        }
      />
    </Stack>
  );
}

type AggData = {
  totalRevenue: number;
  deliveries: number;
  emailDeliveries: number;
  smsDeliveries: number;
  emailRevenue: number;
  smsRevenue: number;
  avgOpenRate: number;
  avgClickRate: number;
  flowCount: number;
} | null;

function comparisonRows(g1: AggData, g2: AggData, t: (key: string, defaultValue: string) => string) {
  const fmt = (v: number | undefined, type: 'currency' | 'number' | 'percent') => {
    if (v == null) return '-';
    if (type === 'currency') return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (type === 'percent') return `${v.toFixed(1)}%`;
    return v.toLocaleString();
  };

  const diff = (v1: number | undefined, v2: number | undefined, type: 'currency' | 'number' | 'percent') => {
    if (v1 == null || v2 == null) return { text: '-', color: 'text.secondary' };
    const d = v1 - v2;
    const pct = v2 !== 0 ? ((d / v2) * 100) : (d > 0 ? 100 : 0);
    const sign = d > 0 ? '+' : '';
    const color = d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary';
    return { text: `${sign}${fmt(d, type)} (${sign}${pct.toFixed(1)}%)`, color };
  };

  const rows: { label: string; g1: string; g2: string; diff: string; diffColor: string; isSection?: boolean }[] = [];

  const add = (label: string, v1: number | undefined, v2: number | undefined, type: 'currency' | 'number' | 'percent', isSection = false) => {
    const d = diff(v1, v2, type);
    rows.push({ label, g1: fmt(v1, type), g2: fmt(v2, type), diff: d.text, diffColor: d.color, isSection });
  };

  add(t('flows.comparison.rows.flows', 'Flows'), g1?.flowCount, g2?.flowCount, 'number', true);
  add(t('flows.metrics.totalRevenue', 'Total Revenue'), g1?.totalRevenue, g2?.totalRevenue, 'currency');
  add(t('flows.metrics.totalDeliveries', 'Total Deliveries'), g1?.deliveries, g2?.deliveries, 'number');
  add(t('flows.metrics.emailRevenue', 'Email Revenue'), g1?.emailRevenue, g2?.emailRevenue, 'currency');
  add(t('flows.metrics.emailDeliveries', 'Email Deliveries'), g1?.emailDeliveries, g2?.emailDeliveries, 'number');
  add(t('flows.metrics.avgOpenRate', 'Avg Open Rate'), g1?.avgOpenRate, g2?.avgOpenRate, 'percent');
  add(t('flows.metrics.avgClickRate', 'Avg Click Rate'), g1?.avgClickRate, g2?.avgClickRate, 'percent');
  add(t('flows.metrics.smsRevenue', 'SMS Revenue'), g1?.smsRevenue, g2?.smsRevenue, 'currency');
  add(t('flows.metrics.smsDeliveries', 'SMS Deliveries'), g1?.smsDeliveries, g2?.smsDeliveries, 'number');

  return rows;
}
